import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  CardActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
} from '@mui/icons-material';
import consultationService from '../services/consultationService';
import { toast } from 'react-toastify';

const ConsultationManagement = () => {
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedConsultation, setSelectedConsultation] = useState(null);
  const [formData, setFormData] = useState({
    label: '',
    description: '',
    prix: '',
    type: 'clinique',
    status: true,
  });

  const fetchConsultations = async () => {
    setLoading(true);
    try {
      const response = await consultationService.getAllConsultations(
        page + 1,
        rowsPerPage
      );
      if (response.success) {
        setConsultations(response.data);
        setError(null);
      }
    } catch (err) {
      setError(err.message || 'Erreur lors du chargement des consultations');
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConsultations();
  }, [page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = (consultation = null) => {
    if (consultation) {
      setSelectedConsultation(consultation);
      setFormData({
        label: consultation.label,
        description: consultation.description,
        prix: consultation.prix,
        type: consultation.type,
        status: consultation.status,
      });
    } else {
      setSelectedConsultation(null);
      setFormData({
        label: '',
        description: '',
        prix: '',
        type: 'clinique',
        status: true,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedConsultation(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    try {
      if (selectedConsultation) {
        await consultationService.updateConsultation(
          selectedConsultation.id,
          formData
        );
        toast.success('Consultation mise à jour avec succès');
      } else {
        await consultationService.createConsultation(formData);
        toast.success('Consultation créée avec succès');
      }
      handleCloseDialog();
      fetchConsultations();
    } catch (error) {
      toast.error(error.message || 'Erreur lors de l\'opération');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette consultation ?')) {
      try {
        await consultationService.deleteConsultation(id);
        toast.success('Consultation supprimée avec succès');
        fetchConsultations();
      } catch (error) {
        toast.error(error.message || 'Erreur lors de la suppression');
      }
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      await consultationService.toggleConsultationStatus(id);
      toast.success('Statut de la consultation modifié avec succès');
      fetchConsultations();
    } catch (error) {
      toast.error(error.message || 'Erreur lors de la modification du statut');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Gestion des Consultations
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Nouvelle Consultation
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Label</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Prix</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : consultations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      Aucune consultation trouvée
                    </TableCell>
                  </TableRow>
                ) : (
                  consultations.map((consultation) => (
                    <TableRow key={consultation.id}>
                      <TableCell>{consultation.label}</TableCell>
                      <TableCell>{consultation.description}</TableCell>
                      <TableCell>{consultation.prix} FCFA</TableCell>
                      <TableCell>
                        {consultation.type === 'clinique' ? 'Clinique' : 'Clinique & Labo'}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={consultation.status ? 'Désactiver' : 'Activer'}>
                          <IconButton
                            onClick={() => handleToggleStatus(consultation.id)}
                            color={consultation.status ? 'success' : 'error'}
                          >
                            {consultation.status ? <ToggleOnIcon /> : <ToggleOffIcon />}
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Modifier">
                          <IconButton
                            onClick={() => handleOpenDialog(consultation)}
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Supprimer">
                          <IconButton
                            onClick={() => handleDelete(consultation.id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={consultations.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Lignes par page"
          />
        </CardContent>
      </Card>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedConsultation ? 'Modifier la Consultation' : 'Nouvelle Consultation'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Label"
                name="label"
                value={formData.label}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Prix"
                name="prix"
                type="number"
                value={formData.prix}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  label="Type"
                >
                  <MenuItem value="clinique">Clinique</MenuItem>
                  <MenuItem value="clinique_labo">Clinique & Labo</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annuler</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {selectedConsultation ? 'Mettre à jour' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConsultationManagement;